/**
 * QA Tools Parameter Comparator
 *
 * QA-specific comparator for report parameters that groups results
 * by sections matching the Report Edit UI.
 *
 * IMPORTANT: This file is only used by QA Tools and does not affect
 * any other part of the application.
 */

import { QaParameterEntry } from './qa-parameter-parser';
import { SECTION_ORDER, SectionName } from './qa-field-mappings';

export interface QaComparisonResult {
  section: SectionName;
  parameter: string; // Display name
  path: string; // Normalized path for uniqueness
  originalPath1: string; // Original path from Report 1
  originalPath2: string; // Original path from Report 2
  value1: string; // Formatted value from Report 1
  value2: string; // Formatted value from Report 2
  match: boolean;
  status: 'match' | 'mismatch' | 'missing-left' | 'missing-right';
}

export interface QaSectionComparison {
  sectionName: SectionName;
  expanded: boolean;
  results: QaComparisonResult[];
  matchCount: number;
  mismatchCount: number;
  missingCount: number;
}

export class QaParameterComparator {
  /**
   * Compare two sets of parameter entries and return grouped comparison results
   */
  static compare(
    entries1: QaParameterEntry[],
    entries2: QaParameterEntry[],
  ): QaSectionComparison[] {
    const comparisonResults: QaComparisonResult[] = [];

    // Create maps for quick lookup using lowercase normalized paths for case-insensitive matching
    // If there are duplicates, keep only the last one
    const map1 = new Map<string, QaParameterEntry>();
    const map2 = new Map<string, QaParameterEntry>();

    entries1.forEach((entry) => map1.set(entry.path.toLowerCase(), entry));
    entries2.forEach((entry) => map2.set(entry.path.toLowerCase(), entry));

    // Get all unique paths (normalized to lowercase)
    const allPaths = new Set([...map1.keys(), ...map2.keys()]);

    // Compare each path
    allPaths.forEach((normalizedPath) => {
      const entry1 = map1.get(normalizedPath);
      const entry2 = map2.get(normalizedPath);

      if (entry1 && entry2) {
        // Both exist - compare values
        const match = this.compareValues(
          entry1.value,
          entry2.value,
          entry1.type,
        );
        comparisonResults.push({
          section: entry1.section,
          parameter: entry1.displayName || entry2.displayName,
          path: entry1.path, // Use normalized path
          originalPath1: entry1.originalPath,
          originalPath2: entry2.originalPath,
          value1: this.formatValue(entry1.value, entry1.type),
          value2: this.formatValue(entry2.value, entry2.type),
          match: match,
          status: match ? 'match' : 'mismatch',
        });
      } else if (entry1) {
        // Only in Report 1
        comparisonResults.push({
          section: entry1.section,
          parameter: entry1.displayName,
          path: entry1.path,
          originalPath1: entry1.originalPath,
          originalPath2: '',
          value1: this.formatValue(entry1.value, entry1.type),
          value2: 'N/A',
          match: false,
          status: 'missing-right',
        });
      } else if (entry2) {
        // Only in Report 2
        comparisonResults.push({
          section: entry2.section,
          parameter: entry2.displayName,
          path: entry2.path,
          originalPath1: '',
          originalPath2: entry2.originalPath,
          value1: 'N/A',
          value2: this.formatValue(entry2.value, entry2.type),
          match: false,
          status: 'missing-left',
        });
      }
    });

    // Group by section using UI section order
    return this.groupBySection(comparisonResults);
  }

  /**
   * Compare two values based on type (case-insensitive for strings, normalized for dates)
   */
  private static compareValues(val1: any, val2: any, type: string): boolean {
    // Null/undefined handling
    if (val1 === null || val1 === undefined) {
      return val2 === null || val2 === undefined;
    }
    if (val2 === null || val2 === undefined) {
      return false;
    }

    // If both are strings
    if (typeof val1 === 'string' && typeof val2 === 'string') {
      const str1 = val1.trim();
      const str2 = val2.trim();

      // If either value contains a time component (timestamp), compare as exact strings
      // This handles schedule fields like "Date to Run" that need timestamp matching
      const hasTimestamp1 = this.hasTimeComponent(str1);
      const hasTimestamp2 = this.hasTimeComponent(str2);

      if (hasTimestamp1 || hasTimestamp2) {
        // Has timestamps - compare exactly (case-insensitive)
        return str1.toLowerCase() === str2.toLowerCase();
      }

      // Try to normalize as dates - this handles cases where type might be 'string'
      // but the values are actually dates (like option values: "2-5-2026" vs "02-05-2026")
      const normalized1 = this.normalizeDate(str1);
      const normalized2 = this.normalizeDate(str2);

      if (normalized1 !== null && normalized2 !== null) {
        return normalized1 === normalized2;
      }

      // Default string comparison (case-insensitive)
      return str1.toLowerCase() === str2.toLowerCase();
    }

    // Handle number/string comparisons (e.g., 42 vs "42")
    if (typeof val1 === 'number' && typeof val2 === 'string') {
      return val1.toString() === val2;
    }
    if (typeof val1 === 'string' && typeof val2 === 'number') {
      return val1 === val2.toString();
    }

    // Strict comparison for other types
    return val1 === val2;
  }

  /**
   * Check if a string contains a time component (hours:minutes)
   */
  private static hasTimeComponent(value: string): boolean {
    // Check for time patterns: HH:MM, H:MM, or T followed by time
    return (
      /T\d{2}:\d{2}/.test(value) || // ISO format: T14:30
      /\d{1,2}:\d{2}:\d{2}/.test(value) || // HH:MM:SS
      /\d{1,2}:\d{2}\s*(AM|PM)/i.test(value)
    ); // 2:30 PM format
  }

  /**
   * Normalize date string to YYYY-MM-DD format for consistent comparison
   * Returns null if the value doesn't look like a date
   */
  private static normalizeDate(value: string): string | null {
    if (!value || typeof value !== 'string') {
      return null;
    }

    const trimmed = value.trim();

    // Try ISO format: YYYY-MM-DDTHH:MM:SS or YYYY-MM-DD
    const isoMatch = trimmed.match(/^(\d{4})-(\d{1,2})-(\d{1,2})/);
    if (isoMatch) {
      const month = isoMatch[2].padStart(2, '0');
      const day = isoMatch[3].padStart(2, '0');
      return `${isoMatch[1]}-${month}-${day}`;
    }

    // Try MM/DD/YYYY format (with slashes)
    const usSlashMatch = trimmed.match(/^(\d{1,2})\/(\d{1,2})\/(\d{2,4})/);
    if (usSlashMatch) {
      const month = usSlashMatch[1].padStart(2, '0');
      const day = usSlashMatch[2].padStart(2, '0');
      let year = usSlashMatch[3];
      if (year.length === 2) {
        year = parseInt(year) > 50 ? `19${year}` : `20${year}`;
      }
      return `${year}-${month}-${day}`;
    }

    // Try MM-DD-YYYY format (with dashes, US format)
    // This handles dates like 01-31-2026 or 1-1-2026
    const usDashMatch = trimmed.match(/^(\d{1,2})-(\d{1,2})-(\d{4})$/);
    if (usDashMatch) {
      const month = usDashMatch[1].padStart(2, '0');
      const day = usDashMatch[2].padStart(2, '0');
      const year = usDashMatch[3];
      return `${year}-${month}-${day}`;
    }

    // Not a recognized date format
    return null;
  }

  /**
   * Format value for display
   */
  private static formatValue(value: any, type: string): string {
    if (value === null || value === undefined) {
      return '';
    }

    if (typeof value === 'boolean') {
      return value.toString();
    }

    if (typeof value === 'number') {
      return value.toString();
    }

    if (typeof value === 'string') {
      return value;
    }

    if (typeof value === 'object') {
      return JSON.stringify(value);
    }

    return String(value);
  }

  /**
   * Group comparison results by section using UI section order
   */
  private static groupBySection(
    results: QaComparisonResult[],
  ): QaSectionComparison[] {
    const sectionMap = new Map<SectionName, QaComparisonResult[]>();

    // Initialize all sections from SECTION_ORDER
    SECTION_ORDER.forEach((section) => {
      sectionMap.set(section, []);
    });

    // Group results by section
    results.forEach((result) => {
      const sectionResults = sectionMap.get(result.section);
      if (sectionResults) {
        sectionResults.push(result);
      } else {
        // Fallback to 'Other' if section not found
        sectionMap.get('Other')!.push(result);
      }
    });

    // Convert to section comparisons with counts, maintaining UI section order
    const sections: QaSectionComparison[] = [];

    SECTION_ORDER.forEach((sectionName) => {
      const sectionResults = sectionMap.get(sectionName) || [];

      // Only include sections that have results
      if (sectionResults.length > 0) {
        const matchCount = sectionResults.filter(
          (r) => r.status === 'match',
        ).length;
        const mismatchCount = sectionResults.filter(
          (r) => r.status === 'mismatch',
        ).length;
        const missingCount = sectionResults.filter(
          (r) => r.status === 'missing-left' || r.status === 'missing-right',
        ).length;

        sections.push({
          sectionName: sectionName,
          expanded: mismatchCount > 0 || missingCount > 0, // Auto-expand sections with differences
          results: sectionResults.sort((a, b) => a.path.localeCompare(b.path)),
          matchCount: matchCount,
          mismatchCount: mismatchCount,
          missingCount: missingCount,
        });
      }
    });

    return sections;
  }

  /**
   * Get summary statistics for all sections
   */
  static getSummary(sections: QaSectionComparison[]): {
    totalMatches: number;
    totalMismatches: number;
    totalMissing: number;
    allMatch: boolean;
  } {
    let totalMatches = 0;
    let totalMismatches = 0;
    let totalMissing = 0;

    sections.forEach((section) => {
      totalMatches += section.matchCount;
      totalMismatches += section.mismatchCount;
      totalMissing += section.missingCount;
    });

    return {
      totalMatches,
      totalMismatches,
      totalMissing,
      allMatch: totalMismatches === 0 && totalMissing === 0,
    };
  }
}
